import { z } from "zod";

const timestamp = z.string().datetime();
const appIdSchema = z.string().trim().min(1).max(128);
const userIdSchema = z.string().trim().min(1).max(128);
const idempotencyKeySchema = z.string().trim().min(8).max(128);
const noteSchema = z.string().trim().max(200).default("");

export const pointLedgerTypeSchema = z.enum(["credit", "debit", "adjust"]);
export type PointLedgerType = z.infer<typeof pointLedgerTypeSchema>;

export const pointAccountSchema = z.object({
  appId: appIdSchema,
  userId: userIdSchema,
  balance: z.number().int().nonnegative(),
  version: z.number().int().positive(),
  updatedAt: timestamp,
});
export type PointAccount = z.infer<typeof pointAccountSchema>;

export const pointEntrySchema = z.object({
  id: z.string().min(1),
  appId: appIdSchema,
  userId: userIdSchema,
  type: pointLedgerTypeSchema,
  delta: z.number().int(),
  balanceAfter: z.number().int().nonnegative().nullable(),
  idempotencyKey: idempotencyKeySchema,
  note: z.string().max(200),
  createdAt: timestamp,
});
export type PointEntry = z.infer<typeof pointEntrySchema>;

export const pointSnapshotSchema = z.object({
  balance: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  updatedAt: timestamp.nullable(),
});
export type PointSnapshot = z.infer<typeof pointSnapshotSchema>;

export const pointMutateInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  amount: z.number().int().min(1).max(1_000_000_000),
  note: noteSchema.optional(),
}).strict();
export type PointMutateInput = z.infer<typeof pointMutateInputSchema>;

export const redeemMembershipWithPointsInputSchema = z.object({
  idempotencyKey: idempotencyKeySchema,
  level: z.string().trim().min(1).max(32).regex(/^[A-Za-z0-9_-]+$/),
  note: noteSchema.optional(),
}).strict();
export type RedeemMembershipWithPointsInput = z.infer<typeof redeemMembershipWithPointsInputSchema>;

export const adminPointAccountSchema = pointAccountSchema.extend({
  appName: z.string().min(1),
  appCode: z.string().min(1),
  userPublicId: z.string().regex(/^\d+$/),
  userName: z.string().min(1),
});
export type AdminPointAccount = z.infer<typeof adminPointAccountSchema>;

export const adminPointAccountListQuerySchema = z.object({
  keyword: z.string().trim().max(100).default(""),
  appId: z.string().trim().max(128).default(""),
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();
export type AdminPointAccountListQuery = z.infer<typeof adminPointAccountListQuerySchema>;

export const adminPointAccountListResultSchema = z.object({
  items: z.array(adminPointAccountSchema),
  total: z.number().int().nonnegative(),
});
export type AdminPointAccountListResult = z.infer<typeof adminPointAccountListResultSchema>;

export const adminPointAccountDetailSchema = z.object({
  account: adminPointAccountSchema,
  entries: z.array(pointEntrySchema),
});
export type AdminPointAccountDetail = z.infer<typeof adminPointAccountDetailSchema>;

export const adminAdjustPointsInputSchema = z.object({
  appId: appIdSchema,
  userId: userIdSchema,
  idempotencyKey: idempotencyKeySchema,
  delta: z.number().int().refine((value) => value !== 0, "调账数量不能为 0").refine((value) => Math.abs(value) <= 1_000_000_000, "调账数量过大"),
  note: z.string().trim().min(1).max(200),
}).strict();
export type AdminAdjustPointsInput = z.infer<typeof adminAdjustPointsInputSchema>;
